/**
 * Column Width Estimation Utility
 *
 * Provides automatic column width estimation based on header text content.
 * Used by `buildColumnDefs` in `use-table-state.ts` as the default sizing
 * strategy when no explicit `width` prop is set on a `<DataTable.Column>`.
 *
 * ## Width Resolution Order
 *
 * 1. Explicit `width` prop on `<DataTable.Column>`
 * 2. Auto-estimated from header text (this module)
 * 3. `defaultColumnSize` prop on `<DataTable>`
 * 4. Global `DEFAULT_COLUMN_SIZE` (150px)
 *
 * ## How It Works
 *
 * 1. `extractTextFromNode()` recursively extracts plain text from the
 *    header's `ReactNode` tree (handles strings, nested elements, fragments).
 * 2. `estimateTextWidth()` sums per-character pixel widths using three
 *    categories (narrow / normal / wide) calibrated for ~14px sans-serif.
 * 3. `estimateColumnWidth()` adds structural padding (cell padding, sort
 *    icon, resize handle, adornment buffer) and enforces a minimum width.
 *
 * ## Tuning Constants
 *
 * All layout constants are derived from the header cell styles in
 * `table-head.tsx`. Adjust these if header padding or icon sizes change:
 *
 * - `HEADER_PADDING` — `px-2.5` × 2 sides = 20px
 * - `SORT_ICON_SPACE` — sort arrow (16px) + gap (6px) = 22px
 * - `RESIZE_HANDLE` — 4px
 * - `HEADER_BUFFER` — 20px extra for inline icons / tooltips
 * - `MIN_ESTIMATED_WIDTH` — 60px floor
 */
import { Children, isValidElement, type ReactNode } from 'react';

// =============================================================================
// Text Width Estimation
// =============================================================================

// Average character widths (in pixels) at ~14px font size for a proportional sans-serif.
// Grouped by visual width category to balance accuracy vs simplicity.
const NARROW_WIDTH = 5; // i l t f j 1 ! | . , : ; ' ( )
const NORMAL_WIDTH = 8; // most lowercase, digits, punctuation
const WIDE_WIDTH = 10; // uppercase, m, w

const NARROW_CHARS = new Set("iltfj1!|.,;:'()");
const WIDE_CHARS = new Set('mwMWABCDEFGHIJKLNOPQRSTUVXYZ');

/**
 * Returns an estimated pixel width for a single character based on its
 * visual width category: narrow (5px), normal (8px), or wide (10px).
 */
function estimateCharWidth(ch: string): number {
  if (ch === ' ') return 4;
  if (NARROW_CHARS.has(ch)) return NARROW_WIDTH;
  if (WIDE_CHARS.has(ch)) return WIDE_WIDTH;
  return NORMAL_WIDTH;
}

/**
 * Estimates the rendered pixel width of a text string using
 * per-character width categories for a proportional sans-serif font.
 *
 * @param text - The plain text string to measure.
 * @returns Estimated width in pixels.
 *
 * @example
 * ```ts
 * estimateTextWidth('COP');            // 30  (all uppercase/wide)
 * estimateTextWidth('Suction Pressure'); // ~122
 * ```
 */
export function estimateTextWidth(text: string): number {
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    width += estimateCharWidth(text[i]);
  }
  return width;
}

// =============================================================================
// ReactNode → Plain Text Extraction
// =============================================================================

/**
 * Recursively extracts plain text from a ReactNode tree.
 *
 * Handles strings, numbers, arrays, fragments, and React elements with
 * children. Non-text nodes (icons, SVGs, etc.) are skipped — only their
 * text children (if any) are included.
 *
 * @param node - Any ReactNode (string, number, element, array, null).
 * @returns The concatenated text content, or `null` if no text is found.
 *
 * @example
 * ```tsx
 * extractTextFromNode('Hello');                        // 'Hello'
 * extractTextFromNode(<span>Header</span>);            // 'Header'
 * extractTextFromNode(<Inline>Name <Icon /></Inline>);  // 'Name'
 * ```
 */
export function extractTextFromNode(node: ReactNode): string | null {
  if (node == null || typeof node === 'boolean') return null;
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);

  // Arrays / fragments
  if (Array.isArray(node)) {
    const parts = node.map(extractTextFromNode).filter(Boolean);
    return parts.length > 0 ? parts.join('') : null;
  }

  // React elements — recurse into children
  if (isValidElement(node)) {
    const children = (node.props as { children?: ReactNode }).children;
    if (children != null) {
      return extractTextFromChildren(children);
    }
  }

  return null;
}

/**
 * Iterates over React children and concatenates their extracted text.
 */
function extractTextFromChildren(children: ReactNode): string | null {
  const parts: string[] = [];
  Children.forEach(children, (child) => {
    const text = extractTextFromNode(child);
    if (text) parts.push(text);
  });
  return parts.length > 0 ? parts.join('') : null;
}

// =============================================================================
// Column Width Estimation
// =============================================================================

// Header cell horizontal padding (px-2.5 = 10px each side)
const HEADER_PADDING = 20;
// Sort indicator icon (w-4 = 16px) + gap (gap-x-1.5 = 6px)
const SORT_ICON_SPACE = 22;
// Resize handle width
const RESIZE_HANDLE = 4;
// Extra buffer for inline icons, tooltips, or other header adornments
const HEADER_BUFFER = 20;
// Minimum sensible column width
const MIN_ESTIMATED_WIDTH = 60;

/**
 * Estimates a column's initial width (in pixels) from its header content.
 *
 * Extracts text from the `ReactNode` header, estimates its pixel width,
 * then adds padding for the header cell's structural elements (padding,
 * sort icon, resize handle, and an adornment buffer for inline icons).
 *
 * Returns `null` when no text can be extracted, allowing the caller to
 * fall back to `defaultColumnSize` or `DEFAULT_COLUMN_SIZE`.
 *
 * @param headerContent - The `ReactNode` passed to `<DataTable.Header>`.
 * @returns Estimated width in pixels, or `null` if text extraction fails.
 *
 * @example
 * ```ts
 * estimateColumnWidth('Name');                         // ~96px
 * estimateColumnWidth(<Inline>Status <Icon /></Inline>); // ~112px
 * estimateColumnWidth(null);                           // null
 * ```
 */
export function estimateColumnWidth(headerContent: ReactNode): number | null {
  const text = extractTextFromNode(headerContent);
  if (!text) return null;

  const textWidth = estimateTextWidth(text);
  const estimated = textWidth + HEADER_PADDING + SORT_ICON_SPACE + RESIZE_HANDLE + HEADER_BUFFER;

  return Math.max(estimated, MIN_ESTIMATED_WIDTH);
}
